import * as React from "react";
import { useState } from "react";
import { useDispatch } from "react-redux";
import Dropzone from "react-dropzone";

import { handleUploadContributeFiles } from "../../store/data/actions";
import CircularProgress from "@mui/material/CircularProgress";
import { FaCloudUploadAlt } from "react-icons/fa";
import { XMarkIcon } from "@heroicons/react/20/solid";
import { Alert, Button, Card, Typography } from "@material-tailwind/react";
import pdfSvg from "./../../assets/svg/pdf.svg";
import docSvg from "./../../assets/svg/doc.svg";
import txtSvg from "./../../assets/svg/txt.svg";
import { toast } from "react-toastify";

export default function ContributeDocs({ docType }) {
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(false);
  const [alertOpen, setAlertOpen] = useState(false);
  const [alertContent, setAlertContent] = useState("");
  const [alertStatus, setAlertStatus] = useState("success");
  const [selectedFiles, setSelectedFiles] = useState(null);

  function getFilePreviewValue(file) {
    const fileType = file.type;
    if (fileType === "application/pdf") {
      return pdfSvg;
    } else if (
      fileType ===
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
    ) {
      return docSvg;
    } else if (fileType === "text/plain") {
      return txtSvg;
    } else {
      return "Preview Not Available";
    }
  }

  function handleAcceptedFiles(files) {
    const updatedFiles = files.map((file, index) => {
      return {
        file: file,
        id: index + Date.now(), // Unique identifier
        name: file.name,
        preview: getFilePreviewValue(file),
        formattedSize: formatBytes(file.size),
      };
    });
    setSelectedFiles(updatedFiles);
  }

  function formatBytes(bytes, decimals = 2) {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const dm = decimals < 0 ? 0 : decimals;
    const sizes = ["Bytes", "KB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"];

    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + " " + sizes[i];
  }

  const handleSubmit = (event) => {
    event.preventDefault(); // Prevent the default form submission behavior
    setIsLoading(true);
    if (selectedFiles?.length) {
      const data = new FormData();
      for (let i = 0; i < selectedFiles.length; i++) {
        const file = selectedFiles[i].file;
        data.append("books", file);
      }
      docType !== undefined &&
        dispatch(handleUploadContributeFiles(data, onUploadCallBack));
    } else {
      return false;
    }
  };

  const onUploadCallBack = ({ status, returnData }) => {
    if (status === "success") {
      if (!returnData.data.message.includes("uploaded 0 file")) {
        setAlertContent(
          `Thank you very much for your document submission! We typically take 24-48 hours to review your submission before we train it. For assistance, please email admin@entheo.info or just ask EntheoGPT!`
        );
        toast.info(returnData.data.message);
        setAlertStatus(status);
      } else {
        setAlertContent(returnData.data.message);
        setAlertStatus("info");
      }

      setSelectedFiles(null);
      setIsLoading(false);
    } else {
      setAlertContent(status);
      setIsLoading(false);
    }
    setIsLoading(false);
    setAlertOpen(true);
  };

  const handleAlertClose = () => {
    setAlertOpen(false);
  };

  function handleDeleteFile(fileId) {
    const updatedFiles = selectedFiles.filter((file) => file.id !== fileId);
    setSelectedFiles(updatedFiles);
  }

  return (
    <div>
      {alertOpen && (
        <Alert
          variant="outlined"
          color={alertStatus === "success" ? "green" : "blue"}
          className="flex justify-between items-center mt-4"
          onClose={handleAlertClose}
        >
          <div>
            <Typography variant="h6" color="inherit">
              {alertStatus &&
                alertStatus[0].toUpperCase() + alertStatus.slice(1)}
            </Typography>
            <Typography>{alertContent}</Typography>
          </div>
        </Alert>
      )}
      <div className="flex flex-col items-start py-3">
        <div className="container px-0 sm:px-16 mb-0">
          <form onSubmit={handleSubmit}>
            <Dropzone
              multiple={true}
              accept={{
                "application/pdf": [".pdf"],
                "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
                  [".docx"],
                "text/plain": [".txt"],
              }}
              onDrop={(acceptedFiles) => {
                handleAcceptedFiles(acceptedFiles);
              }}
            >
              {({ getRootProps, getInputProps }) => (
                <div
                  className="border-2 border-dashed border-gray-300 p-6 rounded-lg text-center cursor-pointer flex flex-col justify-center items-center"
                  {...getRootProps()}
                >
                  <input {...getInputProps()} />
                  <div className="mb-3">
                    <FaCloudUploadAlt className="text-gray-500 text-4xl" />
                  </div>
                  <Typography variant="h6">
                    Drop file here or click to upload.
                  </Typography>
                </div>
              )}
            </Dropzone>
            <div className="mt-8 mb-8">
              {selectedFiles?.map((f, i) => (
                <Card
                  className="flex flex-row items-start sm:items-center justify-between p-4 mb-4 shadow-md"
                  key={i + "-file"}
                >
                  <div className="flex flex-row items-center">
                    <img
                      height="80"
                      className="rounded bg-gray-100 mr-4 w-10 h-auto"
                      alt={f.name}
                      src={f.preview}
                    />
                    <div>
                      <Typography
                        variant="h6"
                        className="text-gray-700 text-sm sm:text-base"
                      >
                        {f.name.length > 20
                          ? f.name.substring(0, 20) + "..."
                          : f.name}
                      </Typography>
                      <Typography
                        variant="small"
                        className="text-gray-500 text-xs sm:text-sm"
                      >
                        {f.formattedSize}
                      </Typography>
                    </div>
                  </div>
                  <span
                    className="cursor-pointer"
                    onClick={() => handleDeleteFile(f.id)}
                  >
                    <XMarkIcon className="h-5 w-5" />
                  </span>
                </Card>
              ))}
            </div>
            <div className="flex justify-center mt-3">
              <Button
                className="flex items-center gap-3"
                color="indigo"
                disabled={isLoading || !selectedFiles?.length}
                type="submit"
              >
                {isLoading ? (
                  <CircularProgress color="inherit" size={20} />
                ) : (
                  <>
                    <FaCloudUploadAlt className="text-xl" />
                    UPLOAD
                  </>
                )}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
